import React, { useEffect, useRef, useState } from 'react'
import type { Flashcard } from '../../../../shared/types'
import api from '../../services/api'

interface FlashcardReviewCardProps {
  flashcard: Flashcard
  isFlipped: boolean
  onFlip: () => void
  autoPlayOnMount?: boolean
  direction?: 'jp-en' | 'en-jp'
  audioOnlyMode?: boolean
  usePremiumEnglishAudio?: boolean
  preferredVoiceId?: string | null
}

export function FlashcardReviewCard({
  flashcard,
  isFlipped,
  onFlip,
  autoPlayOnMount,
  direction = 'jp-en',
  audioOnlyMode = false,
  usePremiumEnglishAudio = false,
  preferredVoiceId = null,
}: FlashcardReviewCardProps) {
  const [isPlayingAudio, setIsPlayingAudio] = useState(false)
  const [isPlayingEnglish, setIsPlayingEnglish] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const englishSpeechRef = useRef<SpeechSynthesisUtterance | null>(null)
  const englishAudioCacheRef = useRef<Record<string, string>>({})
  const premiumEnglishAudioRef = useRef<HTMLAudioElement | null>(null)
  const japaneseAudioCacheRef = useRef<Record<string, string>>({})

  useEffect(() => {
    return () => {
      if (premiumEnglishAudioRef.current) {
        try { premiumEnglishAudioRef.current.pause() } catch { }
        premiumEnglishAudioRef.current = null
      }
      if (englishSpeechRef.current) {
        try { window.speechSynthesis?.cancel() } catch { }
        englishSpeechRef.current = null
      }
    }
  }, [])

  // Play Japanese audio from stored audioUrl
  const playAudio = async () => {
    if ((isPlayingAudio) || (!flashcard.audioUrl && !flashcard.japaneseText)) return
    setIsPlayingAudio(true)
    try {
      // Stop any previous
      if (audioRef.current) {
        try { audioRef.current.pause() } catch { }
        audioRef.current = null
      }

      let clipUrl: string | null = null

      if (usePremiumEnglishAudio && flashcard.japaneseText) {
        const premiumCacheKey = `premium::${flashcard.id ?? flashcard.japaneseText}::${preferredVoiceId || 'default'}`
        let premiumClip = japaneseAudioCacheRef.current[premiumCacheKey]
        if (!premiumClip) {
          try {
            const response = await api.speakJapaneseText(
              flashcard.japaneseText,
              'elevenlabs',
              preferredVoiceId || undefined,
              'ja-JP'
            )
            premiumClip = response?.audioUrl || ''
            if (premiumClip) {
              japaneseAudioCacheRef.current[premiumCacheKey] = premiumClip
            }
          } catch (err) {
            console.error('Failed to fetch premium Japanese audio, falling back:', err)
          }
        }
        clipUrl = premiumClip || null
      }

      if (!clipUrl && flashcard.audioUrl) {
        clipUrl = flashcard.audioUrl
      }

      if (!clipUrl && flashcard.japaneseText) {
        const fallbackCacheKey = `fallback::${flashcard.id ?? flashcard.japaneseText}`
        let fallbackClip = japaneseAudioCacheRef.current[fallbackCacheKey]
        if (!fallbackClip) {
          try {
            const fallbackProvider: 'google' | 'elevenlabs' =
              usePremiumEnglishAudio && preferredVoiceId ? 'elevenlabs' : 'google'
            const fallbackVoiceId =
              fallbackProvider === 'elevenlabs' ? preferredVoiceId || undefined : undefined
            const response = await api.speakJapaneseText(
              flashcard.japaneseText,
              fallbackProvider,
              fallbackVoiceId,
              'ja-JP'
            )
            fallbackClip = response?.audioUrl || ''
            if (fallbackClip) {
              japaneseAudioCacheRef.current[fallbackCacheKey] = fallbackClip
            }
          } catch (err) {
            console.error('Failed to fetch fallback Japanese audio:', err)
          }
        }
        clipUrl = fallbackClip || null
      }

      if (!clipUrl) {
        throw new Error('No Japanese audio available')
      }

      const audio = new Audio(clipUrl)
      audioRef.current = audio
      audio.onended = () => setIsPlayingAudio(false)
      audio.onerror = () => setIsPlayingAudio(false)
      await audio.play()
    } catch (err) {
      console.error('Failed to play audio:', err)
      setIsPlayingAudio(false)
    }
  }

  const playEnglishWithWebSpeech = () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.warn('Speech synthesis unavailable')
      setIsPlayingEnglish(false)
      return
    }

    if (englishSpeechRef.current) {
      try { window.speechSynthesis.cancel() } catch { }
      englishSpeechRef.current = null
    }

    const utterance = new SpeechSynthesisUtterance(flashcard.englishTranslation || '')
    englishSpeechRef.current = utterance
    utterance.lang = 'en-US'
    utterance.rate = 0.9
    utterance.onend = () => {
      setIsPlayingEnglish(false)
      englishSpeechRef.current = null
    }
    utterance.onerror = () => {
      setIsPlayingEnglish(false)
      englishSpeechRef.current = null
    }
    window.speechSynthesis.speak(utterance)
  }

  // Play English audio
  const playEnglishAudio = async () => {
    if (isPlayingEnglish || !flashcard.englishTranslation) return
    setIsPlayingEnglish(true)
    try {
      if (usePremiumEnglishAudio) {
        // Stop any previous premium playback
        if (premiumEnglishAudioRef.current) {
          try { premiumEnglishAudioRef.current.pause() } catch { }
          premiumEnglishAudioRef.current = null
        }

        const cacheKey = flashcard.englishTranslation
        let clip = englishAudioCacheRef.current[cacheKey]
        if (!clip) {
          const response = await api.speakJapaneseText(
            flashcard.englishTranslation,
            'elevenlabs',
            preferredVoiceId || undefined,
            'en-US'
          )
          clip = response?.audioUrl || ''
          englishAudioCacheRef.current[cacheKey] = clip
        }

        if (!clip) {
          throw new Error('No English audio returned')
        }

        const audio = new Audio(clip)
        premiumEnglishAudioRef.current = audio
        audio.onended = () => {
          setIsPlayingEnglish(false)
          premiumEnglishAudioRef.current = null
        }
        audio.onerror = () => {
          setIsPlayingEnglish(false)
          premiumEnglishAudioRef.current = null
        }
        await audio.play()
      } else {
        playEnglishWithWebSpeech()
      }
    } catch (err) {
      console.error('Failed to play English audio:', err)
      if (usePremiumEnglishAudio) {
        try {
          playEnglishWithWebSpeech()
          return
        } catch {
          setIsPlayingEnglish(false)
        }
      } else {
        setIsPlayingEnglish(false)
      }
    }
  }

  // Auto-play audio when card is shown (front side)
  useEffect(() => {
    if (isFlipped) return

    // JP→EN: auto-play Japanese audio
    if (direction === 'jp-en') {
      const t = setTimeout(() => { void playAudio() }, 300)
      return () => clearTimeout(t)
    }

    // EN→JP: auto-play English audio
    if (direction === 'en-jp') {
      const t = setTimeout(() => { void playEnglishAudio() }, 300)
      return () => clearTimeout(t)
    }
  }, [flashcard?.audioUrl, flashcard?.englishTranslation, isFlipped, direction, usePremiumEnglishAudio, preferredVoiceId])

  // Auto-play audio when card is flipped to back side
  useEffect(() => {
    if (!isFlipped) return

    // JP→EN: auto-play English audio on back
    if (direction === 'jp-en') {
      const t = setTimeout(() => { void playEnglishAudio() }, 300)
      return () => clearTimeout(t)
    }

    // EN→JP: auto-play Japanese audio on back
    if (direction === 'en-jp') {
      const t = setTimeout(() => { void playAudio() }, 300)
      return () => clearTimeout(t)
    }
  }, [flashcard?.audioUrl, flashcard?.englishTranslation, isFlipped, direction, usePremiumEnglishAudio, preferredVoiceId])

  // Determine what to show on front and back based on direction
  const frontContent = direction === 'jp-en' ? {
    main: flashcard.japaneseText,
    sub: flashcard.furigana,
    hasAudio: true
  } : {
    main: flashcard.englishTranslation,
    sub: null,
    hasAudio: false
  }

  const backContent = direction === 'jp-en' ? {
    main: flashcard.englishTranslation,
    example: flashcard.exampleSentence,
    exampleTranslation: flashcard.exampleTranslation
  } : {
    main: flashcard.japaneseText,
    example: flashcard.exampleSentence,
    exampleTranslation: flashcard.exampleTranslation
  }

  const frontFace = audioOnlyMode ? (
    <div>
      <div className="text-6xl mb-8">🔊</div>
      <div className="text-2xl font-semibold text-gray-700 mb-4">Listen and recall</div>
      <div className="mt-8 text-sm text-gray-400">Tap to reveal answer</div>
    </div>
  ) : (
    <div>
      <div
        className={`font-bold text-gray-900 mb-4 ${direction === 'jp-en' ? 'text-5xl' : 'text-3xl'}`}
        lang={direction === 'jp-en' ? (flashcard.targetLanguage || 'ja') : 'en'}
      >
        {frontContent.main}
      </div>
      {frontContent.sub && (
        <div className="text-xl text-gray-500 mb-8" lang={flashcard.targetLanguage || 'ja'}>
          {
            (() => {
              const f = frontContent.sub || ''
              return /[A-Za-z]/.test(f) ? '' : f
            })()
          }
        </div>
      )}

      <div className="mt-8 text-sm text-gray-400">Tap to reveal answer</div>
    </div>
  )

  const backFace = (
    <div className="text-center">
      <div
        className={`font-semibold text-gray-900 mb-6 ${direction === 'en-jp' ? 'text-5xl' : 'text-3xl'}`}
        lang={direction === 'en-jp' ? (flashcard.targetLanguage || 'ja') : 'en'}
      >
        {backContent.main}
      </div>

      {direction === 'en-jp' && flashcard.furigana && (
        <div className="text-xl text-gray-500 mb-6" lang={flashcard.targetLanguage || 'ja'}>
          {
            (() => {
              const f = flashcard.furigana || ''
              return /[A-Za-z]/.test(f) ? '' : f
            })()
          }
        </div>
      )}

      <div className="border-t border-gray-200 pt-6 mt-6">
        <div className="text-lg text-gray-700 mb-2 italic" lang={flashcard.targetLanguage || 'ja'}>
          {backContent.example}
        </div>
        <div className="text-sm text-gray-500">{backContent.exampleTranslation}</div>
      </div>

      <div className="mt-8">
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            flashcard.masteryLevel === 'new'
              ? 'bg-blue-100 text-blue-700'
              : flashcard.masteryLevel === 'learning'
                ? 'bg-yellow-100 text-yellow-700'
                : flashcard.masteryLevel === 'review'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
          }`}
        >
          {flashcard.masteryLevel.charAt(0).toUpperCase() + flashcard.masteryLevel.slice(1)}
        </span>
      </div>

      <div className="mt-6 text-xs text-gray-400">Rate your recall below</div>
    </div>
  )

  return (
    <div className="premium-review-card relative rounded-3xl border-2 border-gray-200 shadow-xl overflow-hidden cursor-pointer transition-shadow duration-500 hover:shadow-2xl p-6">
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary-300 via-secondary-300 to-primary-400" />
      <div className="absolute top-4 right-4 flex gap-2 z-20">
        {flashcard.audioUrl && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              void playAudio()
            }}
            disabled={isPlayingAudio}
            className="p-3 rounded-full bg-primary-100 text-primary-600 hover:bg-primary-200 transition disabled:opacity-50"
            aria-label="Play Japanese pronunciation"
            title="Play Japanese pronunciation"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              {isPlayingAudio ? (
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                  clipRule="evenodd"
                />
              )}
            </svg>
          </button>
        )}
        {flashcard.englishTranslation && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              void playEnglishAudio()
            }}
            disabled={isPlayingEnglish}
            className="p-3 rounded-full bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition disabled:opacity-50"
            aria-label="Play English audio"
            title="Play English audio"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              {isPlayingEnglish ? (
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M5 8.25A.75.75 0 015.75 7.5h2.086a.75.75 0 00.53-.22l1.72-1.72A.75.75 0 0111.31 5h2.94A.75.75 0 0115 5.75v8.5a.75.75 0 01-.75.75H5.75A.75.75 0 015 14.25v-6zM13 8a1 1 0 00-1.447-.894l-3 1.5A1 1 0 008 9.5v1a1 1 0 00.553.894l3 1.5A1 1 0 0013 12V8z"
                  clipRule="evenodd"
                />
              )}
            </svg>
          </button>
        )}
      </div>

      <div
        className="flashcard-3d flashcard-3d-review min-h-[380px]"
        onClick={(event) => {
          event.stopPropagation()
          onFlip()
        }}
      >
        <div className={`flashcard-3d-inner ${isFlipped ? 'is-flipped' : ''}`}>
          <div className="flashcard-face flashcard-face-front px-8 py-12 text-center">{frontFace}</div>
          <div className="flashcard-face flashcard-face-back px-8 py-12">{backFace}</div>
        </div>
      </div>
    </div>
  )
}
